import * as React from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  TextField,
} from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

type StoreOption = { label: string; value: string };

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ALL_OPTION: StoreOption = {
  label: 'Select all stores',
  value: '__ALL__',
};

export interface StoreMultiSelectProps {
  allStores: string[];
  value: string[];
  onChange: (next: string[]) => void;
  width?: number | string;
  label?: string;
  selectedStores: string[];
}

export const StoreMultiSelect: React.FC<StoreMultiSelectProps> = ({
  allStores,
  value,
  onChange,
  width = 400,
  label = 'Stores',
  selectedStores,
}) => {
  const selectedValues = React.useMemo(
    () => (value.length > 0 ? value : selectedStores),
    [selectedStores, value],
  );

  const storeOptions = React.useMemo<StoreOption[]>(
    () =>
      allStores
        .map((s) => ({ label: s, value: s }))
        .sort((a, b) => Number(a.value) - Number(b.value)),
    [allStores],
  );

  const options = React.useMemo<StoreOption[]>(
    () => [ALL_OPTION, ...storeOptions],
    [storeOptions],
  );

  const selectedOptions = React.useMemo<StoreOption[]>(
    () => storeOptions.filter((o) => selectedValues.includes(o.value)),
    [selectedValues, storeOptions],
  );

  const allSelected =
    storeOptions.length > 0 && selectedOptions.length === storeOptions.length;

  const handleChange = (_e: unknown, newOptions: StoreOption[]) => {
    const last = newOptions[newOptions.length - 1];
    if (last?.value === ALL_OPTION.value) {
      onChange(allSelected ? [] : storeOptions.map((o) => o.value));
      return;
    }

    const cleaned = newOptions
      .filter((o) => o.value !== ALL_OPTION.value)
      .map((o) => o.value);
    onChange(cleaned);
  };

  return (
    <FormControl sx={{ width }}>
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        disableCloseOnSelect
        filterSelectedOptions={false}
        includeInputInList
        isOptionEqualToValue={(o, v) => o.value === v.value}
        getOptionLabel={(o) => o.label}
        ListboxProps={{ style: { maxHeight: 360, overflow: 'auto' } }}
        renderTags={(tagValue) => {
          if (tagValue.length === 0) return [];
          return [
            <Chip
              key="summary-chip"
              label={`${tagValue.length} store${
                tagValue.length > 1 ? 's' : ''
              } selected`}
            />,
          ];
        }}
        renderOption={(props, option, { selected }) => {
          const effectiveSelected =
            option.value === ALL_OPTION.value ? allSelected : selected;
          return (
            <Box component="li" {...props} key={option.value}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={effectiveSelected}
              />
              {option.label}
              {option.value === ALL_OPTION.value && storeOptions.length > 0 && (
                <Box component="span" sx={{ ml: 1, opacity: 0.6 }}>
                  (
                  {allSelected
                    ? 'unselect all'
                    : `select all ${storeOptions.length}`}
                  )
                </Box>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder="Search store number"
          />
        )}
      />
    </FormControl>
  );
};
